import { useNavigate, useParams } from "react-router";
import { useContext, useEffect, useState } from "react";
import { Timestamp, doc, getDoc, setDoc } from "firebase/firestore";
import { fireDB } from "../../firebase/FirebaseConfig";
import toast from "react-hot-toast";
import Loader from "../../components/Loader/Loader";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import MyContext from "../../context/Context";

const UpdateResort = () => {
  const { loading, setLoading, getAllResortFunction } = useContext(MyContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [resort, setResort] = useState({
    name: "",
    location: "",
    guests: "",
    nights: "",
    price: "",
    description: "",
    facilities: "",
    menu: "",
    roomRates: "",
    inclusions: "",
    weddingBudget: "",
    image: "",
    gallery: [],
    packages: [],
  });

  const [imageFiles, setImageFiles] = useState([]);

  // ✅ Load Single Resort
  useEffect(() => {
    const fetchResort = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(fireDB, "resorts", id));
      if (snap.exists()) {
        const data = snap.data();

        setResort({
          ...data,
          facilities: Array.isArray(data.facilities)
            ? data.facilities.join(", ")
            : "",
          menu: Array.isArray(data.menu) ? data.menu.join(", ") : "",
          roomRates: Array.isArray(data.roomRates)
            ? data.roomRates.join(", ")
            : "",
          inclusions: Array.isArray(data.inclusions)
            ? data.inclusions.join(", ")
            : "",
        });
      }

      } catch (error) {
        console.error(error);
        toast.error("Failed to load resort");
      } finally {
        setLoading(false);
      }
    };

    fetchResort();
  }, [id]);

  // ✅ Upload New Images
  const uploadImagesAndGetUrls = async () => {
    const storage = getStorage();
    const urls = [];

    for (const file of imageFiles) {
      if (file) {
        const storageRef = ref(
          storage,
          `resortImages/${Date.now()}-${file.name}`
        );
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        urls.push(downloadURL);
      }
    }

    return urls;
  };

  // ✅ Update Resort
  const updateResort = async () => {
    if (!resort.name || !resort.location || !resort.price) {
      return toast.error("Required fields missing");
    }

    setLoading(true);

    try {
      const newImageUrls = await uploadImagesAndGetUrls();

  const updatedData = {
    ...resort,
    facilities: resort.facilities
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean),

    menu: resort.menu
      .split(",")
      .map((m) => m.trim())
      .filter(Boolean),

    roomRates: resort.roomRates
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean),

    inclusions: resort.inclusions
      .split(",")
      .map((i) => i.trim())
      .filter(Boolean),

    gallery: [...(resort.gallery || []), ...newImageUrls],
    image: newImageUrls[0] || resort.image,
    updatedAt: Timestamp.now(),
  };


      await setDoc(doc(fireDB, "resorts", id), updatedData, {
        merge: true,
      });

      toast.success("Resort updated successfully");
      getAllResortFunction();
      navigate("/adminDashboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update resort");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Remove Existing Gallery Image
  const removeImage = (index) => {
    const updatedGallery = resort.gallery.filter((_, i) => i !== index);
    setResort({ ...resort, gallery: updatedGallery });
  };

return (
  <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-6">
    {loading && (
      <div className="fixed inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center">
        <Loader />
      </div>
    )}

    <div className="w-full max-w-3xl bg-white shadow-2xl rounded-2xl p-8 border border-gray-200">
      <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">
        Update Resort
      </h2>

      {/* Resort Name */}
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Resort Name
      </label>
      <input
        type="text"
        value={resort.name}
        onChange={(e) => setResort({ ...resort, name: e.target.value })}
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition shadow-sm"
      />

      {/* Location */}
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Location
      </label>
      <input
        type="text"
        value={resort.location}
        onChange={(e) => setResort({ ...resort, location: e.target.value })}
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 transition shadow-sm"
      />

      {/* Guests & Nights */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Guests
          </label>
          <input
            type="number"
            value={resort.guests}
            onChange={(e) => setResort({ ...resort, guests: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Nights
          </label>
          <input
            type="number"
            value={resort.nights}
            onChange={(e) => setResort({ ...resort, nights: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm"
          />
        </div>
      </div>

      {/* Price */}
      <label className="block text-sm font-semibold text-gray-700 mt-4 mb-1">
        Price
      </label>
      <input
        type="text"
        value={resort.price}
        onChange={(e) => setResort({ ...resort, price: e.target.value })}
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm"
      />

      {/* Wedding Budget */}
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Wedding Budget
      </label>
      <input
        type="text"
        value={resort.weddingBudget}
        onChange={(e) =>
          setResort({ ...resort, weddingBudget: e.target.value })
        }
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm"
      />

      {/* Description */}
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Description
      </label>
      <textarea
        rows="4"
        value={resort.description}
        onChange={(e) => setResort({ ...resort, description: e.target.value })}
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm resize-none"
      />

      {/* Facilities */}
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Facilities (comma separated)
      </label>
      <input
        type="text"
        value={resort.facilities}
        onChange={(e) => setResort({ ...resort, facilities: e.target.value })}
        className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition shadow-sm"
      />

      {/* Existing Gallery */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">
          Gallery Images
        </h3>

        <div className="grid grid-cols-3 gap-4">
          {resort.gallery?.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt=""
                className="w-full h-24 object-cover rounded-lg shadow"
              />
              
              <button
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Add New Images */}
      <input
        type="file"
        multiple
        onChange={(e) => setImageFiles([...e.target.files])}
        className="w-full mt-4 text-sm text-gray-600
        file:mr-4 file:py-2 file:px-4
        file:rounded-lg file:border-0
        file:text-sm file:font-semibold
        file:bg-blue-50 file:text-blue-700
        hover:file:bg-blue-100"
      />

      {/* Packages */}
      <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-700">
        Packages
      </h3>

      {resort.packages.map((pkg, index) => (
        <div key={index} className="bg-gray-50 p-4 rounded-xl mb-4 border">
          <input
            type="text"
            placeholder="Package Title"
            value={pkg.title}
            onChange={(e) => {
              const updated = [...resort.packages];
              updated[index].title = e.target.value;
              setResort({ ...resort, packages: updated });
            }}
            className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />

          <textarea
            placeholder="Package Description"
            value={pkg.description}
            onChange={(e) => {
              const updated = [...resort.packages];
              updated[index].description = e.target.value;
              setResort({ ...resort, packages: updated });
            }}
            className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="text"
            placeholder="Package Price"
            value={pkg.price}
            onChange={(e) => {
              const updated = [...resort.packages];
              updated[index].price = e.target.value;
              setResort({ ...resort, packages: updated });
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />

          {resort.packages.length > 1 && (
            <button
              type="button"
              onClick={() => {
                const updated = resort.packages.filter((_, i) => i !== index);
                setResort({ ...resort, packages: updated });
              }}
              className="text-red-500 text-sm mt-2 hover:underline"
            >
              Remove Package
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          setResort({
            ...resort,
            packages: [
              ...resort.packages,
              { title: "", description: "", price: "" },
            ],
          })
        }
        className="text-blue-600 text-sm hover:underline mb-6"
      >
        + Add Package
      </button>

      <button
        onClick={updateResort}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-xl font-semibold shadow-lg transition duration-300"
      >
        Update Resort
      </button>
    </div>
  </div>
);

};

export default UpdateResort;
